import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function WandsworthCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Wandsworth Pothole Claims | 372km U-Roads on 2021 Survey Data | Fixtyer"
        description="Wandsworth projects £10.25m in capital highway spend against a £655k DfT allocation, yet the DfT rates condition RED and flags incomplete road condition data. See what the council's own transparency report means for your pothole claim."
        canonical="/councils/wandsworth"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Wandsworth', url: '/councils/wandsworth' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Wandsworth has GREEN spend and is tripling capital investment — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because Wandsworth projects £10.25m capital spend against a £655,000 DfT allocation in 2025/26, with preventative maintenance estimated at 86.34%. But the Condition scorecard is RED — the DfT flags Wandsworth\'s ratings as based on incomplete road condition data — and the council\'s last published network DVI dates from 2021/22. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on forward spend alone.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 372.18km — roughly 91% of Wandsworth\'s 409.2km adopted carriageway network. The council\'s last Detailed Visual Inspection in 2021/22 found 17% of unclassified roads had defects requiring intervention such as resurfacing — about 63km of residential streets. Annex B submits RAG percentages to the DfT for principal and non-principal classified roads only; no equivalent 130-series breakdown for U-roads appears in the published report.',
          },
          {
            question: 'Why does the DfT rate Wandsworth\'s condition RED when the council says it has London\'s biggest resurfacing programme?',
            answer: 'The DfT notes that Wandsworth\'s overall, condition and best-practice scorecards are based on incomplete road condition data. The transparency report cites only a 2021/22 DVI for borough-wide intervention percentages and admits the highway asset has been in "managed decline" for years. Capital spend is projected to rise from £4.75m to £10.25m — but that is forward investment against a condition picture the council has not fully re-surveyed since 2021/22.',
          },
          {
            question: 'Does Wandsworth sharing highway services with Richmond affect my claim?',
            answer: 'Wandsworth and Richmond upon Thames operate a Shared Service Arrangement for all highway maintenance functions, with performance directly compared between boroughs. Liability for your claim still rests with Wandsworth Borough Council as the highway authority for roads in this borough. The shared-service model means Wandsworth may invoke Richmond-benchmarked processes under Section 58 — but you should still challenge whether those systems caught your specific defect.',
          },
          {
            question: 'Wandsworth fixes 100% of dangerous potholes within seven days — does that block my claim?',
            answer: 'It helps the council only if they can prove your defect was classified as dangerous — over 40mm deep under the council\'s published threshold — notified through Brightly Confirm or another logged channel, and repaired within seven days before your incident. KPI WECSHOS-004 showed 100% in Q1 and Q2 of 2025/26. Prior reports, photos showing defect age, and proof the pothole predated any inspection all matter when a council points to response-time KPIs.',
          },
          {
            question: 'My pothole was on a red route — is that still Wandsworth?',
            answer: 'No. Wandsworth maintains approximately 93% of the borough\'s highway network; Transport for London maintains the remaining approximately 7% as red routes. Both the council\'s report-a-pothole page and its 7 rings, 7 days guarantee direct residents to report defects on red routes to TfL, not the borough. Check the road classification before you claim.',
          },
          {
            question: 'Does the managed decline admission help my claim?',
            answer: 'It can. Wandsworth\'s June 2025 report states its highway asset "has been in a managed decline position for a number of years" and that there is "an ever-increasing gap between asset needs and available budgets." Causeway Asset Management modelling in 2024/25 showed "significant additional investment is required to improve Network Performance." When a council documents managed decline and a funding gap, it raises questions about whether reactive pothole patching satisfies the Section 41 duty for your specific road.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Wandsworth Borough Council"
        councilSlug="wandsworth"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/wandsworth"
        headline="Wandsworth Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Wandsworth</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Wandsworth: 372km of U-Roads on 2021 Survey Data
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Wandsworth earns <strong className="text-emerald-400">GREEN</strong> for spend — projecting
            {' '}<strong className="text-emerald-400">£10.25m</strong> in capital works against a
            {' '}<strong className="text-emerald-400">£655,000</strong> DfT allocation. Yet the DfT awards
            {' '}<strong className="text-rose-400">RED</strong> for condition, notes ratings are
            {' '}<strong className="text-rose-400">based on incomplete road condition data</strong>, and the
            council's last borough-wide Detailed Visual Inspection — from <strong className="text-rose-400">2021/22</strong> —
            flagged <strong className="text-rose-400">36% of A-roads</strong> and
            {' '}<strong className="text-rose-400">56% of classified roads</strong> for resurfacing-level intervention
            on a network where <strong className="text-rose-400">372.18km is unclassified</strong>.
          </p>

          {/* Key stat callout */}
          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">372km</div>
            <div className="text-white font-medium mt-2">Unclassified roads — 91% of the carriageway network</div>
            <div className="text-brand-300 text-sm mt-1">
              Wandsworth's June 2025 transparency report lists 372.18km of U-roads against just 21.76km of
              A-roads and 15.26km of B and C roads. The last published intervention-rate survey for this
              overwhelmingly residential network dates from 2021/22 — four years before this report.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network overview & DfT caveat */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            A 409km Network — DfT RED Condition, Incomplete Data
          </h2>
          <p className="text-surface-600 mb-8">
            Wandsworth Borough Council's own highway inventory from Annex A (June 2025), set against DfT 2025/26 scorecards
          </p>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
              <RatingBadge rating="amber" size="md" />
              <div className="text-sm font-semibold text-amber-900 mt-3">Overall — AMBER</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-5 text-center">
              <RatingBadge rating="red" size="md" />
              <div className="text-sm font-semibold text-rose-900 mt-3">Condition — RED</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center">
              <RatingBadge rating="green" size="md" />
              <div className="text-sm font-semibold text-emerald-900 mt-3">Spend — GREEN</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
              <RatingBadge rating="amber" size="md" />
              <div className="text-sm font-semibold text-amber-900 mt-3">Best practice — AMBER</div>
            </div>
          </div>

          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-8">
            <p className="text-rose-800 text-sm">
              The Department for Transport notes that Wandsworth's overall, condition and best-practice scorecards
              are <strong>based on incomplete road condition data</strong>, which has affected these ratings. That
              is the government's own caveat — not our characterisation — and it sits alongside a RED condition
              scorecard.
            </p>
          </div>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Classification</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Length (km approx.)</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Share of network</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center font-mono">21.76</td>
                  <td className="px-6 py-4 text-center">5.3%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center font-mono">15.26</td>
                  <td className="px-6 py-4 text-center">3.7%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-bold text-rose-900">Unclassified roads</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">372.18</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-700">90.9%</td>
                </tr>
                <tr className="border-t border-surface-200">
                  <td className="px-6 py-4 font-medium text-brand-900">Footways</td>
                  <td className="px-6 py-4 text-center font-mono">834.28</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Cycleways</td>
                  <td className="px-6 py-4 text-center font-mono">3.89*</td>
                  <td className="px-6 py-4 text-center text-surface-400 text-xs">*Provisional</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-surface-600 text-sm mt-6">
            Wandsworth maintains approximately 440km of roads in total — council responsibility is approximately 93%,
            TfL red routes approximately 7%. Gross replacement cost for carriageways alone is £692,693,000; total
            maintenance spend in 2025/26 represents 0.15% of current carriageway and footway asset value per Annex B.
          </p>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            The last Detailed Visual Inspection was in 2021/22 — supplemented by DfT single-data-list submissions
            in Annex B (October 2025)
          </p>

          {/* DVI intervention table */}
          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <div className="bg-surface-100 px-6 py-3 border-b border-surface-200">
              <h3 className="font-semibold text-brand-900">2021/22 DVI — roads requiring intervention (resurfacing-level defects)</h3>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Network length</th>
                  <th className="px-6 py-3 text-center font-semibold text-rose-700">Requiring intervention</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Approx. km affected</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center">21.76 km</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">36%</td>
                  <td className="px-6 py-4 text-center text-surface-600">~7.8 km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B roads (within classified total)</td>
                  <td className="px-6 py-4 text-center">—</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">20%</td>
                  <td className="px-6 py-4 text-center text-surface-600">—</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-bold text-amber-900">All classified (A and B roads combined)</td>
                  <td className="px-6 py-4 text-center font-bold">37.02 km</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">56%</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-700">~20.7 km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">Unclassified roads</td>
                  <td className="px-6 py-4 text-center">372.18 km</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                  <td className="px-6 py-4 text-center text-surface-600">~63.3 km</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Pavements / footways</td>
                  <td className="px-6 py-4 text-center">834.28 km</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">31%</td>
                  <td className="px-6 py-4 text-center text-surface-600">~258.6 km</td>
                </tr>
              </tbody>
            </table>
            <div className="px-6 py-4 bg-amber-50 border-t border-amber-200">
              <p className="text-amber-800 text-sm">
                These figures measure defects requiring intervention such as resurfacing — not the same
                RED/Amber/Green categories used in Annex B's DfT data-list submissions. Wandsworth has not
                published an updated DVI since 2021/22 in this report. Approximate km affected derived from
                published percentages × network length where both are available.
              </p>
            </div>
          </div>

          {/* Annex B RAG data */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-brand-900 mb-4">Annex B — DfT single data list (where maintenance should be considered)</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-5 border border-surface-200">
                <div className="text-sm text-surface-500 mb-2">130-01 Principal roads</div>
                <div className="flex gap-6">
                  <div>
                    <div className="text-2xl font-bold text-rose-600">8.4%</div>
                    <div className="text-xs text-rose-800">Red</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-amber-600">36.1%</div>
                    <div className="text-xs text-amber-800">Amber</div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-surface-200">
                <div className="text-sm text-surface-500 mb-2">130-02 Non-principal classified roads</div>
                <div className="flex gap-6">
                  <div>
                    <div className="text-2xl font-bold text-rose-600">6.88%</div>
                    <div className="text-xs text-rose-800">Red</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-amber-600">25.23%</div>
                    <div className="text-xs text-amber-800">Amber</div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 border border-surface-200">
                <div className="text-sm text-surface-500 mb-2">130-03 Skidding resistance</div>
                <div className="flex gap-6">
                  <div>
                    <div className="text-2xl font-bold text-rose-600">28.4%</div>
                    <div className="text-xs text-rose-800">Red</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-emerald-600">71.6%</div>
                    <div className="text-xs text-emerald-800">Green</div>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-surface-600 text-sm mt-4">
              No equivalent 130-series RAG breakdown for unclassified roads appears in Annex B — despite U-roads
              being 90.9% of the carriageway network. Combined, 44.5% of principal roads and 32.11% of
              non-principal classified roads are in RED or amber condition where maintenance should be considered.
            </p>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Wandsworth must show it had a reasonable system for knowing
              the condition of its roads. Ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and was it before or after 2021/22?</li>
              <li>• If 17% of U-roads already needed intervention at the last survey, what was done about yours?</li>
              <li>• Why has no updated DVI been published if AI surveys are still being trialled for 2026/27?</li>
              <li>• Does the dangerous-pothole KPI cover your defect, or only defects reported through Brightly Confirm?</li>
              <li>• How does the DfT's incomplete-data flag align with a blanket Section 58 defence?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed network knowledge from a four-year-old survey while the DfT
              rates its condition data incomplete and awards RED for condition.
            </p>
          </div>
        </div>
      </section>

      {/* Managed decline */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Managed Decline Admission
          </h2>
          <p className="text-surface-600 mb-8">
            Wandsworth's own explanation for why condition lags investment — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The Council highway asset, like many other Highway Authorities' highway asset, has been in a managed decline position for a number of years. There has been a sustained challenges in highway maintenance investment going back many years with an ever-increasing gap between asset needs and available budgets."
              source="Wandsworth Borough Council Local Highways Maintenance Transparency Report — Annex A (June 2025)"
            />
            <QuoteCard
              quote="The modelling showed significant additional investment is required to improve Network Performance."
              source="Wandsworth Borough Council Local Highways Maintenance Transparency Report — Annex A (June 2025), citing Causeway Asset Management Consultant modelling in 2024/25"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What this admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Wandsworth formally acknowledges its highway asset has been in managed decline — accepting
                deterioration rather than keeping pace with need. The council also states that once a road
                reaches a bad enough condition, "more expensive structural repairs, complete replacement or
                even closure are likely to follow."
              </p>
              <p className="text-surface-600 text-sm">
                When a council documents managed decline and an investment gap, it raises questions about
                whether reactive pothole patching satisfies the Section 41 duty for your specific road.
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">The investment response</h3>
              <p className="text-emerald-800 text-sm mb-3">
                The council's additional investment increased from £4.75m to £8m in 2024/25 and to £10.25m
                per annum in future years — which the report states would "both stop the trend of continual
                annual decline in the network condition and enable significant improvements over time."
              </p>
              <p className="text-emerald-800 text-sm">
                That is a plan — not proof your road was maintained before your incident. Forward spend
                does not retroactively fix a four-year-old condition survey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            GREEN Spend, RED Condition
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of Wandsworth's own spending data — and the sharp 2024/25 to 2025/26 uplift
          </p>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">DfT capital (£000s)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Capital spend (£m)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Revenue spend (£m)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-emerald-700">Preventative</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">£0</td>
                  <td className="px-6 py-4 text-center">£3.50</td>
                  <td className="px-6 py-4 text-center">£1.10</td>
                  <td className="px-6 py-4 text-center">68.57%</td>
                  <td className="px-6 py-4 text-center text-rose-600">31.42%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">£202</td>
                  <td className="px-6 py-4 text-center">£4.75</td>
                  <td className="px-6 py-4 text-center">£1.20</td>
                  <td className="px-6 py-4 text-center">74.73%</td>
                  <td className="px-6 py-4 text-center text-rose-600">25.26%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">£202</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">£8.00</td>
                  <td className="px-6 py-4 text-center">£1.30</td>
                  <td className="px-6 py-4 text-center">83.75%</td>
                  <td className="px-6 py-4 text-center text-rose-600">16.25%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2025/26 (proj.)</td>
                  <td className="px-6 py-4 text-center font-bold">£655</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">£10.25</td>
                  <td className="px-6 py-4 text-center font-bold">£1.40</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">86.34%</td>
                  <td className="px-6 py-4 text-center font-bold">13.66%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-2xl font-bold mb-4">More Than Fifteen Times The DfT Allocation</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£655k</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£10.25m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">86.34%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Wandsworth plans to spend more than <span className="text-emerald-400">fifteen times its DfT allocation</span> —
              yet the DfT still rates condition <span className="text-rose-400">RED</span> and flags incomplete
              condition data. Revenue funding is "spent on reactive maintenance such as potholes and defective
              paving repairs" while capital funds resurfacing and reconstruction.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2025/26 DfT-funded programme</h3>
              <p className="text-surface-600 text-sm mb-3">
                The council's £655,000 DfT allocation in 2025/26 is allocated to specific schemes including:
              </p>
              <ul className="text-surface-600 text-sm space-y-1">
                <li>• Queenstown Road (A3216) Phase 2 — Queen's Circus to Sopwith Way</li>
                <li>• Balham Park Road — St James's Drive to O/S No. 71</li>
                <li>• Putney Hill (A219) Phase 2 — Lytton Grove to Wildcroft Road</li>
                <li>• Garratt Lane (A217) Phase 2 footway renewal — Allfarthing Lane to Magdalen Road</li>
              </ul>
              <p className="text-surface-500 text-xs mt-3 italic">
                If your pothole was not on a listed scheme, the council may still argue routine inspection —
                but the programme shows where DfT money is actually going.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Council marketing vs DfT ratings</h3>
              <p className="text-amber-800 text-sm mb-3">
                Wandsworth's transparency web page states the borough is running "the biggest resurfacing
                programme of any borough in London" in 2025/26, with the largest portion council-funded.
              </p>
              <p className="text-amber-800 text-sm">
                That is the council's own characterisation. The DfT's independent scorecard — with its
                incomplete-data caveat — tells a more cautious story about whether the network condition
                picture supports a strong Section 58 defence on your specific road.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Shared service & TfL */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Shared Services, TfL Gaps and Inspection KPIs
          </h2>
          <p className="text-surface-600 mb-8">
            How Wandsworth maintains its network — and what the council documents about funding shortfalls
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Wandsworth and Richmond-upon-Thames operate a Shared Service Arrangement for all Highway Maintenance functions, so the performance of each borough is directly compared."
              source="Wandsworth Borough Council Local Highways Maintenance Transparency Report — Annex B (October 2025)"
            />
            <QuoteCard
              quote="TfL funding for the BPRN continues to fall short of what is needed, which has had a negative effect on the condition of the Borough's A roads."
              source="Wandsworth Borough Council Local Highways Maintenance Transparency Report — Annex A (June 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">TfL BPRN funding history</h3>
              <p className="text-surface-600 text-sm mb-3">
                Wandsworth previously received an annual TfL grant for the Borough Principal Road Network until
                2018/19. TfL funding has been "considerably reduced" and is now limited to a central amount
                boroughs must bid for competitively.
              </p>
              <ul className="text-surface-600 text-sm space-y-1">
                <li>• No BPRN funding: 2019/20, 2020/21, 2022/23, 2024/25</li>
                <li>• £200,000 secured: 2021/22, 2023/24, 2025/26</li>
              </ul>
              <p className="text-surface-600 text-sm mt-3">
                With 36% of A-roads flagged for intervention at the last DVI and 8.4% in RED on the DfT data
                list, the council documents underfunding of its most trafficked routes.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Inspection and response KPIs (Q1–Q2 2025/26)</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div className="bg-brand-900/50 rounded-xl p-5">
                <div className="text-3xl font-bold text-emerald-400">100%</div>
                <div className="text-brand-300 text-sm mt-1">WECSHOS-002: dangerous highway defects attended within 24 hours</div>
              </div>
              <div className="bg-brand-900/50 rounded-xl p-5">
                <div className="text-3xl font-bold text-emerald-400">100%</div>
                <div className="text-brand-300 text-sm mt-1">WECSHOS-004: dangerous potholes fixed within 7 days</div>
              </div>
              <div className="bg-brand-900/50 rounded-xl p-5">
                <div className="text-3xl font-bold text-amber-400">2026/27</div>
                <div className="text-brand-300 text-sm mt-1">PAS 2161 methodology due — AI surveys still being trialled</div>
              </div>
            </div>
            <p className="text-brand-200 mb-4">
              Public enquiries and defect reports are logged on the Brightly Confirm Customer Services Module.
              "The numbers of Carriageway or Footway Defects is one of Factors that are included" in maintenance
              programme selection. As part of the Pride in Our Streets programme, the council launched its
              7 rings, 7 days guarantee for dangerous potholes.
            </p>
            <p className="text-white font-medium">
              Prior reports through Brightly Confirm, FixMyStreet or the council's online form are direct
              evidence of notice — especially where the council's own KPIs apply only to defects classified
              as dangerous.
            </p>
          </div>
        </div>
      </section>

      {/* What this means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a RED-Condition, GREEN-Spend Borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Wandsworth is investing heavily — but condition data tells a different story
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council's favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital projected at £10.25m against £655k DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Preventative share rising to 86.34% in 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 100% dangerous-defect and dangerous-pothole KPIs in Q1–Q2 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> FM Conway contract, UKPMS DVI surveys and published annual maintenance programme
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> LoTAG/LoHEG benchmarking and shared-service performance comparison with Richmond
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence citing inspection regimes and response KPIs. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">What works in yours</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED condition scorecard — DfT flags incomplete road condition data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Last network DVI from 2021/22 — 56% of classified roads flagged for intervention
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits "managed decline" and an ever-increasing investment gap
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 372km of U-roads with no Annex B RAG breakdown — 17% needed intervention at last survey
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> TfL A-road funding "falls short" — 36% of A-roads needed intervention at last DVI
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Dangerous-pothole KPI applies only to defects over 40mm — not every claim-level pothole
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a borough with GREEN spend and 100% dangerous-pothole KPIs, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (Brightly Confirm, FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the 2021/22 survey gap and 17% intervention rate are structural arguments</li>
              <li>• Whether your street was on the 2025/26 DfT-funded programme list or outside the pipeline</li>
              <li>• Whether the defect met the council's 40mm dangerous threshold — and if not, why it was left untreated</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Wandsworth's own transparency data — including managed decline and the DfT incomplete-data
              caveat — where they help you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Report a pothole in Wandsworth
          </h2>
          <p className="text-surface-600 mb-8">
            Reporting the defect creates a council record — useful evidence whether or not you claim
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Council reporting channels</h3>
              <p className="text-surface-600 text-sm mb-4">
                Wandsworth states that any defect reported will be inspected and, if appropriate, programmed
                for repair. Dangerous potholes — judged as a difference of level or trip of over 40mm — fall
                under the 7 rings, 7 days guarantee.
              </p>
              <ul className="text-sm space-y-2">
                <li>
                  <a
                    href="https://www.wandsworth.gov.uk/roads-and-transport/report-a-pothole/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:underline font-medium"
                  >
                    Report a pothole online
                  </a>
                  <span className="text-surface-500"> — name, location and description required</span>
                </li>
                <li>
                  <a
                    href="https://www.wandsworth.gov.uk/listening/#7_rings,_7_days"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:underline font-medium"
                  >
                    7 rings, 7 days guarantee
                  </a>
                  <span className="text-surface-500"> — dangerous defect threshold and WhatsApp reporting</span>
                </li>
                <li>
                  <a
                    href="https://www.wandsworth.gov.uk/roads-and-transport/report-a-street-problem/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:underline font-medium"
                  >
                    Report a street problem
                  </a>
                  <span className="text-surface-500"> — broader defects including footways</span>
                </li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Red routes — report to TfL</h3>
              <p className="text-amber-800 text-sm mb-3">
                Wandsworth maintains approximately 93% of borough roads. Defects on Transport for London red
                routes — approximately 7% of the network — must be reported to TfL, not the borough council.
              </p>
              <p className="text-amber-800 text-sm">
                If you are claiming for damage, confirm the highway authority first. A pothole on a Wandsworth
                borough road supports a claim against Wandsworth Borough Council; a red-route defect goes to TfL.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-xl p-6">
            <p className="text-brand-200 text-sm">
              Defect reports logged through Brightly Confirm feed into maintenance prioritisation. A dated report
              with photos is stronger evidence than an undocumented pothole the council can say it never knew about.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Wandsworth?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded borough with RED condition data demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No 2021/22 survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No DfT incomplete-data citation</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 372km U-road network and stale survey documented</li>
                <li>✅ RED condition / incomplete data argued</li>
                <li>✅ Managed decline admission cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Wandsworth</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=wandsworth"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Wandsworth Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Wandsworth has GREEN spend and is tripling capital investment — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because Wandsworth projects £10.25m capital spend against a
                £655,000 DfT allocation in 2025/26, with preventative maintenance estimated at 86.34%. But the
                Condition scorecard is RED — the DfT flags Wandsworth's ratings as based on incomplete road
                condition data — and the council's last published network DVI dates from 2021/22. Section 58 turns
                on whether the specific defect that damaged your vehicle was reasonably inspected and repaired,
                not on forward spend alone.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 372.18km — roughly 91% of Wandsworth's 409.2km adopted carriageway network. The
                council's last Detailed Visual Inspection in 2021/22 found 17% of unclassified roads had defects
                requiring intervention such as resurfacing — about 63km of residential streets. Annex B submits RAG
                percentages to the DfT for principal and non-principal classified roads only; no equivalent
                130-series breakdown for U-roads appears in the published report.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why does the DfT rate Wandsworth's condition RED when the council says it has London's biggest resurfacing programme?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT notes that Wandsworth's overall, condition and best-practice scorecards are based on
                incomplete road condition data. The transparency report cites only a 2021/22 DVI for borough-wide
                intervention percentages and admits the highway asset has been in "managed decline" for years.
                Capital spend is projected to rise from £4.75m to £10.25m — but that is forward investment against
                a condition picture the council has not fully re-surveyed since 2021/22.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Wandsworth sharing highway services with Richmond affect my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Wandsworth and Richmond upon Thames operate a Shared Service Arrangement for all highway maintenance
                functions, with performance directly compared between boroughs. Liability for your claim still rests
                with Wandsworth Borough Council as the highway authority for roads in this borough. The shared-service
                model means Wandsworth may invoke Richmond-benchmarked processes under Section 58 — but you should
                still challenge whether those systems caught your specific defect.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Wandsworth fixes 100% of dangerous potholes within seven days — does that block my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It helps the council only if they can prove your defect was classified as dangerous — over 40mm deep
                under the council's published threshold — notified through Brightly Confirm or another logged channel,
                and repaired within seven days before your incident. KPI WECSHOS-004 showed 100% in Q1 and Q2 of
                2025/26. Prior reports, photos showing defect age, and proof the pothole predated any inspection all
                matter when a council points to response-time KPIs.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                My pothole was on a red route — is that still Wandsworth?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Wandsworth maintains approximately 93% of the borough's highway network; Transport for London
                maintains the remaining approximately 7% as red routes. Both the council's report-a-pothole page and
                its 7 rings, 7 days guarantee direct residents to report defects on red routes to TfL, not the borough.
                Check the road classification before you claim.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the managed decline admission help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. Wandsworth's June 2025 report states its highway asset "has been in a managed decline position
                for a number of years" and that there is "an ever-increasing gap between asset needs and available
                budgets." Causeway Asset Management modelling in 2024/25 showed "significant additional investment is
                required to improve Network Performance." When a council documents managed decline and a funding gap,
                it raises questions about whether reactive pothole patching satisfies the Section 41 duty for your
                specific road.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.wandsworth.gov.uk/highways-maintenance-transparency-report/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Wandsworth Borough Council Local Highways Maintenance Transparency Report (June &amp; October 2025)
            </a>{' '}
            |{' '}
            <a
              href="https://www.wandsworth.gov.uk/media/hnbhwsqi/wandsworth_local_highway_maintenance_transparency_report_annex_a_june_2025.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Annex A (June 2025)
            </a>{' '}
            |{' '}
            <a
              href="https://www.wandsworth.gov.uk/media/idpfp3d5/wandsworth_local_highway_maintenance_transparency_report_annex_b_october_2025.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Annex B (October 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
